import { ProductCatalogTitleRenderer } from 'components/FieldRenderers/Renderers/productMetadata'
import {
    eans,
    fsaHsaEligible,
    gtins,
    sustainability,
    upcs,
} from 'configuration/fieldCreators/marketAnalysisSegments'
import { asProductInfoPlaceholder } from 'configuration/fieldCreators/productMetadata'
import { OTHER } from 'const/formatting'
import { Field } from 'types'
import { ChartFormatters } from 'types/dataSources'

import { createDateField, createField } from '../fieldCreators/createField'

// Hulk's `sales_estimates` is aggregated weekly (Sunday–Saturday). Only the
// `week` dimension is exposed; higher grains (month/quarter/year) are not
// true re-aggregations, so they are intentionally omitted.
export const MARKET_ANALYSIS_SEGMENTS_TIME_AGGREGATE_COLUMNS: Field<unknown>[] =
    [
        createDateField({
            id: 'week',
            name: 'Week',
            shortName: 'Week',
            dataIndex: ['week'],
            minWidth: 80,
            width: 110,
            dateAggregation: 'week',
        }),
    ]

// `product_info` is a synthetic group by — it isn't a hulk dimension. The
// hulk widget API strips it before issuing the query, but its dependencies
// (`asin`, `title_stable`, `image_url_hero`) are real dimensions that hulk
// returns alongside each row. translateHulkRecord renames title_stable →
// title and image_url_hero → image_url so ProductCatalogTitleRenderer's
// existing fallbacks pick them up.
export const MARKET_ANALYSIS_SEGMENTS_PRODUCT_INFO_PLACEHOLDER: Field<unknown> =
    asProductInfoPlaceholder(
        createField({
            id: 'product_info',
            name: 'Product',
            shortName: 'Product',
            minWidth: 120,
            dataIndex: ['asin'],
            renderOptions: {
                render: (props) =>
                    ProductCatalogTitleRenderer<any>({
                        ...props,
                        showImage: true,
                    }),
            },
            width: 300,
        })
    )

function asinField(): Field<unknown> {
    return createField({
        id: 'asin',
        name: 'ASIN',
        shortName: 'ASIN',
        dataIndex: ['asin'],
    })
}

function titleStableField(): Field<unknown> {
    return createField({
        id: 'title_stable',
        name: 'Title',
        shortName: 'Title',
        dataIndex: ['title_stable'],
    })
}

function imageUrlHeroField(): Field<unknown> {
    return createField({
        id: 'image_url_hero',
        name: 'Image',
        shortName: 'Image',
        dataIndex: ['image_url_hero'],
    })
}

export const MARKET_ANALYSIS_SEGMENTS_PRODUCT_INFO_DEPENDENCIES: Field<unknown>[] =
    [asinField(), titleStableField(), imageUrlHeroField()]

// Array-of-string dimensions returned by hulk for `sales_estimates`. The
// generic `metadata_format` fall-through in MetricsGlossary/queries.ts has no
// branch for arrays, so without these the cells render as `[object Object]`-ish
// noise. CommaListRenderer joins with commas, truncates with ellipsis, and
// shows the full list in a tooltip on hover.
export const SPECIAL_MARKET_ANALYSIS_SEGMENTS_GROUP_BY_COLUMNS: Field<unknown>[] =
    [eans(), gtins(), upcs(), sustainability(), fsaHsaEligible()]

const MAX_CHART_TITLE_LENGTH = 30

// Category charts read the group by value straight off the record to name each
// series. `product_info` is synthetic and never on the record, so without a
// displayName every slice falls back to "N/A". Derive the name from the real
// dimensions hulk returns (title_stable → title, plus asin).
function formatMarketAnalysisProductName(
    record: Record<string, unknown>
): string {
    const asin = typeof record.asin === 'string' ? record.asin : undefined
    // Hulk's `include_others` rollup sets every product dimension to the
    // literal string "Others" (and a real product always carries an asin), so
    // either signal identifies the rollup. Returning OTHER keeps the name a
    // bare "Others" so CODED_COLORS renders the slice gray — the Forge
    // convention for the others series.
    if (!asin || asin === OTHER) {
        return OTHER
    }
    const rawTitle =
        (typeof record.title === 'string' && record.title) ||
        (typeof record.title_stable === 'string' && record.title_stable) ||
        ''
    if (!rawTitle) {
        return asin
    }
    const title =
        rawTitle.length > MAX_CHART_TITLE_LENGTH
            ? `${rawTitle.slice(0, MAX_CHART_TITLE_LENGTH)}…`
            : rawTitle
    return `${title} (${asin})`
}

export const MARKET_ANALYSIS_SEGMENTS_GROUP_BY_CONFIG: ChartFormatters = {
    product_info: {
        displayName: (record) => formatMarketAnalysisProductName(record),
        color: (record, { colorMapper }) =>
            colorMapper(typeof record.asin === 'string' ? record.asin : OTHER),
    },
}
